import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Platform, View, type ListRenderItem } from 'react-native'

import { AnimatedParallax } from '@/src/elements/animated-parallax-list/AnimatedParallaxList'
import { DonationButton } from '@/src/elements/button/donation/DonationButton'
import { BasicHeader } from '@/src/elements/header/BasicHeader'
import { Header } from '@/src/elements/header/Header'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import { DONATION_SOURCE } from '@/src/features/donation/api/types/donation'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useBackHandlePress } from '@/src/general/hooks/useBackHandlePress'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import { useTagQuery } from '../../../../tag/services/useTagQuery'
import type { TCommunityApiHasJoinedInfos, TMediumCommunityApi } from '../../../api/communityApi.types'
import type { EVENT_ORIGIN } from '../../../models/community.constants'
import type { TCommunitiesCategoryTagImageProps } from '../../components/CommunitiesCategory/CommunitiesCategoryTagImage'
import { CommunityCard } from '../../components/CommunityCard'
import { CommunitySearchPlaceholder } from '../../components/CommunitySearchPlaceholder'

const ItemSeparatorComponent = () => <Spacer y={4} />

type TCommunitiesCategoryMobileProps = TCommunitiesCategoryTagImageProps &
  Pick<
    UseInfiniteQueryResult,
    'error' | 'fetchNextPage' | 'fetchStatus' | 'hasNextPage' | 'isFetching' | 'isFetchingNextPage' | 'isPending'
  > & {
    data?: (TMediumCommunityApi & TCommunityApiHasJoinedInfos)[]
    origin: EVENT_ORIGIN
    source: TRACKING_SOURCE
  }

export const CommunitiesCategoryMobile = ({
  data: communities,
  error,
  isFetching,
  isPending,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  ImageComponent,
  tag: tagParam,
  customTagName,
  origin,
  source,
}: TCommunitiesCategoryMobileProps): ReactNode => {
  const { data: tag } = useTagQuery(tagParam || '', !ImageComponent)
  const { routes } = useRoutes()
  const { handleBackPress } = useBackHandlePress(routes.communities())
  const isNative = Platform.OS !== 'web'

  const renderItem: ListRenderItem<NonNullable<typeof communities>[number]> = ({ item }) => (
    <View className="native:h-[340px] bg-white px-4">
      <CommunityCard fullWidth community={item} origin={origin} source={source} />
    </View>
  )

  const ListFooter = () => {
    if (isFetching || isPending) {
      return Array.from({ length: 4 }, (_, index) => (
        <View key={index} className="px-4" testID={TEST_ID.COMMUNITIES.CATEGORIES_MOBILE_LOADING}>
          <CommunitySearchPlaceholder />
        </View>
      ))
    }
    return null
  }

  return (
    <AnimatedParallax.List
      testID={TEST_ID.MISCELLANEOUS.CATEGORY_MOBILE_LIST}
      imageUri={getImageUrl(tag?.icon)}
      ImageComponent={ImageComponent}
      ListHeaderComponent={() => (
        <AnimatedParallax.Header
          imageUri={getImageUrl(tag?.icon)}
          ImageComponent={ImageComponent}
          CustomHeader={
            isNative ? (
              <Header
                className="top-safe-insets-t absolute left-0 right-0 z-50"
                leftButtons={[
                  {
                    type: 'icon',
                    startIcon: ICON.ARROW_LEFT,
                    onPress: handleBackPress,
                    colors: 'primary',
                    variant: 'faded',
                  },
                ]}
                rightButtons={[
                  {
                    type: 'button',
                    component: () => <DonationButton donationSource={DONATION_SOURCE.HEADER} isIconOnly />,
                  },
                ]}
              />
            ) : (
              <BasicHeader goBackTo={routes.communities()} />
            )
          }
          content={
            <Typography
              size="xl"
              black
              className={clsx(
                'absolute bottom-0 left-0 right-0 mx-20 mb-12 bg-transparent text-center text-white',
                customTagName?.className,
              )}
            >
              {customTagName?.text || tag?.name}
            </Typography>
          }
        />
      )}
      data={communities}
      renderItem={renderItem}
      onEndReached={() => {
        if (!isFetchingNextPage && communities && hasNextPage) {
          fetchNextPage()
        }
      }}
      ListEmptyComponent={error && <ErrorMessage error={error} defaultErrorKey="community.load.error" />}
      keyExtractor={(item) => item.id.toString()}
      ItemSeparatorComponent={ItemSeparatorComponent}
      ListFooterComponent={ListFooter}
    />
  )
}
